import { shiftLinesUp } from "./inputHandler.js";
import { columns, rows } from "./setup.js";
import { state } from "./state.js";
import { screen } from "./screen.js";
import { stringToCells, cellsToString, alignCells } from "./cell.js";
import { DEFAULT_BG, DEFAULT_FG, parseColorValue } from "./colors.js";

export const printLine = async (
  line = "",
  {
    charDelay = 0,
    lineDelay = 0,
    align = 'left',
    endWithEnter = true,
    preventCursorShift = false
  } = {}
) => {
  // Convert the input line to a cell array.
  let cellLine = typeof line === "string" ? stringToCells(line) : line;
  // Align the cells based on the specified alignment.
  if (align !== 'left')
    cellLine = alignCells(cellLine, columns, align);
  
  // If a character delay is set, type the line character by character.
  if (charDelay > 0) {
    return new Promise(resolve => {
      let charIndex = 0;
      function typeNextChar() {
        if (charIndex < cellLine.length) {
          // Append each character (cell) to the position of the cursor.
          screen.lines[screen.cursorPosition[0]][screen.cursorPosition[1]] = cellLine[charIndex];
          // Move the cursor to the next position.
          screen.cursorPosition[1]++;
          // If the cursor position exceeds the number of columns, move to the next line.
          if (screen.cursorPosition[1] >= columns) {
            screen.cursorPosition[0]++;
            screen.cursorPosition[1] = 0;
          }
          // If the cursor position exceeds the number of rows, move all lines up one line and reset the cursor position to last line.
          if (!preventCursorShift && screen.cursorPosition[0] >= rows) {
              shiftLinesUp();
          }
          charIndex++;
          setTimeout(typeNextChar, charDelay);
        } else {
          if (endWithEnter) {
            // Update the cursor position, so it's at the beginning of the next line.
            screen.cursorPosition[0]++;
            screen.cursorPosition[1] = 0;
            // If the cursor position exceeds the number of rows, move all lines up one line and reset the cursor position to last line.
            if (screen.cursorPosition[0] >= rows) {
                shiftLinesUp();
            }
          }
          setTimeout(resolve, lineDelay);
        }
      }
      typeNextChar();
    });
  } else {
    // Pad the line to the number of columns.
    cellLine = alignCells(cellLine, columns, 'left');
    
    // If no character delay is set, print the line instantly.
    return new Promise(resolve => {
      // Set the line to the line where the cursor is.
      screen.lines[screen.cursorPosition[0]] = cellLine;
      // If a line delay is set, wait for the delay before resolving.
      setTimeout(() => {
        // If endWithEnter is false, do not move the cursor to the next line.
        if (endWithEnter) {
          // Update the cursor position, so it's at the beginning of the next line.
          screen.cursorPosition[0]++;
          screen.cursorPosition[1] = 0;
          // If the cursor position exceeds the number of rows, move all lines up one line and reset the cursor position to last line.
          if (screen.cursorPosition[0] >= rows) {
              shiftLinesUp();
          } 
        } else if (!preventCursorShift) {
          // Move the cursor to the end of the line. As the current line is always padded, we need to get the string value of the line.
          const len = cellsToString(cellLine).trim().length;
          // Move the cursor to the end of the line.
          screen.cursorPosition[1] = len;
          // If the cursor position exceeds the number of columns, move to the next line.
          if (screen.cursorPosition[1] >= columns) {
              screen.cursorPosition[0]++;
              screen.cursorPosition[1] = 0;
          }
        }

        resolve();
      }, lineDelay);
    });
  }
};

export const pause = (delay, callback = null) => {
  return new Promise(resolve => {
    setTimeout(() => {
      if (callback) {
        callback();
      }
      resolve();
    }, delay);
  });
};

export const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const _waitForAnyKeyPress = () => new Promise((resolve) => {
    state.waitingForKeyPress = true; // Set the flag
    state.keyPressResolve = resolve; // Store the resolve function
});

export const waitForAnyKeyPress = async (color) => {
  state.blockCursor = false;
  if (color) {
    // Print a message in the specified color.
    await printLine(parseStyledString(`[fg=${color}]PRESS ANY KEY TO CONTINUE...[/]`, DEFAULT_FG, DEFAULT_BG), { endWithEnter: false });
  } else {
    // Print a default message.
    await printLine("PRESS ANY KEY TO CONTINUE...", { endWithEnter: false });
  }
  await _waitForAnyKeyPress();
  state.blockCursor = true;
};

  /**
 * Parses a string with simple inline markup that allows you to set foreground (fg)
 * and background (bg) colors for segments of text.
 *
 * Markup example:
 *    "Hello [fg=yellow]World[/]!"
 *
 * In this example, "Hello " and "!" will use the default colors, and "World" will
 * use yellow as the foreground.
 *
 * @param {string} input - The input string containing markup.
 * @param {string} defaultFG - The default foreground color.
 * @param {string} defaultBG - The default background color.
 * @returns {Cell[]} A composite cell array representing the parsed text.
 */
export const parseStyledString = (input, defaultFG = DEFAULT_FG, defaultBG = DEFAULT_BG) => {
  const result = [];
  let currentIndex = 0;
  // This regex finds segments that begin with [<attributes>] and end with [/].
  //   Group 1: The attributes e.g., "fg=yellow" or "fg=yellow,bg=blue"
  //   Group 2: The text within the tags.
  const regex = /\[([^\]]+)\](.*?)\[\/\]/gs;
  let match;

  while ((match = regex.exec(input)) !== null) {
    // First, add any plain text preceding this styled segment.
    if (match.index > currentIndex) {
      const plainText = input.slice(currentIndex, match.index);
      result.push(...stringToCells(plainText, defaultFG, defaultBG));
    }
    
    // Process the attributes.
    const attributes = match[1].split(",").map(attr => attr.trim());
    let fg = defaultFG;
    let bg = defaultBG;
    attributes.forEach(attr => {
      const [key, value] = attr.split("=");
      if (key && value) {
        if (key.toLowerCase() === "fg") {
          const parsed = parseColorValue(value);
          if (parsed) {
            fg = parsed;
          }
        } else if (key.toLowerCase() === "bg") {
          const parsed = parseColorValue(value);
          if (parsed) {
            bg = parsed;
          }
        }
      }
    });
    
    // Convert the styled text segment into an array of Cells.
    const styledText = match[2];
    result.push(...stringToCells(styledText, fg, bg));
    
    // Update the current index to the end of this match.
    currentIndex = regex.lastIndex;
  }
  
  // Append any remaining plain text after the last match.
  if (currentIndex < input.length) {
    const plainText = input.slice(currentIndex);
    result.push(...stringToCells(plainText, defaultFG, defaultBG));
  }
  
  return result;
};

export const stripFormattingCodes = (input) => input.replace(/\[.*?\]/g, ""); // Removes all formatting codes

/**
 * Evaluates a mathematical expression safely with a given context.
 * Supports both synchronous and asynchronous functions in the context.
 *
 * @param {string} expr - The mathematical expression to evaluate.
 * @param {object} context - An object containing variables or functions to use in the evaluation.
 * @returns {Promise<*>} The result of the evaluated expression.
 */
export const safeEval = async (expr, context = {}) => {
  // Validate the expression.
  // Allow digits, decimal points, spaces, arithmetic operators, parentheses, variable/function names, and strings in double quotes.
  // Exclude content inside double quotes from validation.
  const strippedExpr = expr.replace(/"[^"]*"/g, '""'); // Replace content inside double quotes with empty quotes.
  if (!/^[\d+\-*/%().\s\w"$]+$/.test(strippedExpr)) {
    throw new Error("Expression contains illegal characters.");
  }

  // Match all function calls in the expression (e.g., ABS(-3)).
  const functionCallRegex = /(\w+\$?)\(([^()]+)\)/g;
  let match;

  // Resolve all function calls asynchronously.
  while ((match = functionCallRegex.exec(expr)) !== null) {
    const [fullMatch, functionName, args] = match;

    // Check if the function exists in the context.
    if (context[functionName]) {
      // Call the function with the evaluated arguments.
      const resolvedValue = await context[functionName](await safeEval(args, context));

      // Ensure the resolved value is properly quoted if it's a string.
      const resolvedString = typeof resolvedValue === "string" ? `"${resolvedValue}"` : resolvedValue;

      // Replace the function call in the expression with its resolved value.
      expr = expr.replace(fullMatch, resolvedString);

      // Reset the regex search to ensure all function calls are processed.
      functionCallRegex.lastIndex = 0;
    } else {
      throw new Error(`Function "${functionName}" not found in context.`);
    }
  }

  // Evaluate the final expression synchronously.
  const contextKeys = Object.keys(context);
  const contextValues = Object.values(context);
  const func = new Function(...contextKeys, `return ${expr};`);

  // Evaluate the expression and handle concatenation of strings and numbers.
  const result = func(...contextValues);

  // If the result is a string, remove the surrounding quotes.
  if (typeof result === "string") {
    return result.replace(/^"|"$/g, "");
  }

  return result;
};